import React from 'react';

import classnames from 'classnames';

import {Dialog} from 'shells/dialog/dialog';
import {ErrorDialog} from 'shells/dialog/error-dialog';
import {Switch} from 'shells/switch/switch';
import {Body} from 'shells/typography/body';

import type {LicenseType} from 'nutshell-graphql-types';
import type {
    SetLicensesForUserInput,
    GetUsersByFilter_usersByFilter_edges_node as User,
} from 'nutshell-graphql-types';

import {useHandleLicenseToggle} from '../../../hooks/use-handle-license-toggle';

// @ts-expect-error
import {useSetLicensesForUser} from '../../licensing/graphql/hooks';

type Props = {
    user: User;
    licenseType: LicenseType;
    isOwned: boolean;
    refetchLicenseOverview: () => void;
    refetchUsers: () => Promise<any>;
    isPurchased: boolean;
    displayName: string;
    isTrial: boolean;
};

export function LicenseToggleComponent(props: Props) {
    const [isOn, setIsOn] = React.useState<boolean>(props.isOwned);
    const [showDialog, setShowDialog] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);

    // The toggles should only be disabled if we aren't a trial and the license
    // isn't purchased
    const isDisabled = (!props.isPurchased && !props.isTrial) || !props.user.isEnabled;

    const {setLicensesForUser} = useSetLicensesForUser();
    const {handleLicenseChange} = useHandleLicenseToggle();

    const switchRef = React.useRef(null);

    const handleSwitchToggle = () => {
        setShowDialog(true);
    };

    const handleConfirmDialog = () => {
        setShowDialog(false);
        const input: SetLicensesForUserInput = {
            id: props.user.id,
            licenses: [{licenseType: props.licenseType, isEnabled: !isOn}],
        };
        // @ts-expect-error
        setLicensesForUser(input).then((res) => {
            const response =
                res &&
                res.data &&
                res.data.setLicensesForUser &&
                res.data.setLicensesForUser.licenseUpdateResponse;

            if (response.wasSuccessful) {
                props.refetchLicenseOverview();
                props.refetchUsers();
                handleLicenseChange({
                    licenseState: {
                        isEngagementOn: props.licenseType === 'ENGAGEMENT' ? isOn : undefined,
                    },
                    licenseType: props.licenseType,
                    userId: props.user.id,
                });
            } else {
                // @ts-expect-error
                setIsOn(response.newLicenseState.some((l) => l === props.licenseType));
                setErrorMessage(
                    response.errorMessage || 'An error occurred while updating the license.'
                );
                setShowErrorDialog(true);
            }
        });
    };

    const handleCancelDialog = () => {
        setShowDialog(false);
    };

    const switchStyleNames = classnames('flex', 'justify-sb', 'align-center', 'gap-4', {
        disabled: isDisabled,
    });

    return (
        <div ref={switchRef}>
            <div className={switchStyleNames}>
                <Switch
                    isOn={isOn}
                    isRounded={true}
                    onChange={handleSwitchToggle}
                    shouldAnimate={false}
                    isDisabled={isDisabled}
                    isSmall={true}
                />
                <Body size='small'>{isOn ? 'On' : 'Off'}</Body>
            </div>
            {showDialog && (
                <Dialog
                    anchor={switchRef.current}
                    question={`Are you sure you want to turn ${isOn ? 'off' : 'on'} this ${
                        props.displayName
                    } license?`}
                    primaryText={`Yes, turn ${isOn ? 'off' : 'on'}`}
                    secondaryText={`No, leave it ${isOn ? 'on' : 'off'}`}
                    onPrimary={handleConfirmDialog}
                    onSecondary={handleCancelDialog}
                    onBlur={handleCancelDialog}
                    primaryVariant={isOn ? 'danger' : 'save'}
                />
            )}
            {showErrorDialog && (
                <ErrorDialog
                    anchor={switchRef.current}
                    message={errorMessage}
                    onClose={() => setShowErrorDialog(false)}
                />
            )}
        </div>
    );
}
